import { Link } from "react-router-dom";
import { ArrowRight, BatteryCharging, Zap, Timer, Bot, Route as RouteIcon, Wifi, SunMedium, Leaf, ShieldCheck, Gauge, Wrench } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "@/components/Reveal";
import HotspotView from "@/components/HotspotView";
import { motion } from "framer-motion";
import SpinView from "@/components/SpinView";
import Countdown from "@/components/Countdown";

const RANGE = [
  { mode: "City", hindi: "शहर", km: "780 km", note: "Regen braking के साथ सबसे ज़्यादा" },
  { mode: "Highway", hindi: "राजमार्ग", km: "650 km", note: "Cruise पर efficient खपत" },
  { mode: "Combined", hindi: "संयुक्त", km: "700+ km", note: "ARAI मानक — दावा नहीं, आँकड़ा" },
];

const CHARGING = [
  {
    icon: Zap,
    title: "DC Fast Charging",
    big: "10–80% in 30 min",
    desc: "150 kW charger पर आधे घंटे में आधा India पार — chai से कम, कॉफ़ी से ज़्यादा नहीं।",
  },
  {
    icon: BatteryCharging,
    title: "Home Wallbox · 11 kW AC",
    big: "Overnight full",
    desc: "सोने से पहले लगाओ, उठते-उठते पूरा चार्ज। हर दिन नए सिरे से।",
  },
  {
    icon: SunMedium,
    title: "Solar Assist Roof",
    big: "+15 km / day",
    desc: "धूप जितनी मिलेगी, रेंज उतनी मुफ़्त — battery को धीरे-धीरे खुद चार्ज करती है।",
  },
];

const INTELLIGENCE = [
  { icon: Bot, title: "AI Copilot", desc: "आपकी आदतें सीखता सहायक — route, climate, charging सब संभाले।" },
  { icon: RouteIcon, title: "Auto Drive Mode", desc: "Highway पर steering, speed और lane — Auto Drive खुद संभाले। आप relax करें।" },
  { icon: Wifi, title: "Smart Connectivity", desc: "App से remote charging, pre-cooling, location — सब aapke phone पर।" },
];

const SPECS = [
  { icon: BatteryCharging, value: "77 kWh", label: "LFP Battery" },
  { icon: Gauge, value: "250 kW", label: "340 bhp Motor · RWD" },
  { icon: Timer, value: "5.9 s", label: "0–100 km/h" },
  { icon: RouteIcon, value: "700+ km", label: "ARAI Range" },
  { icon: ShieldCheck, value: "15 yr", label: "Lifetime Battery Warranty" },
  { icon: Leaf, value: "0 g/km", label: "Tailpipe Emissions" },
  { icon: Wrench, value: "₹300", label: "Lifetime Servicing — हर service" },
];

function Hero() {
  return (
    <section className="relative overflow-hidden pt-36 pb-16" data-testid="electric-hero">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(ellipse, rgba(230,184,0,0.14), transparent 62%)" }}
        aria-hidden="true"
      />
      <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-5 sm:px-8 lg:grid-cols-2">
        <Reveal>
          <p className="eyebrow">IshuCar Electric · 100% Electric</p>
          <h1 className="mt-4 font-display text-[clamp(1.5rem,6vw,3rem)] font-extrabold uppercase leading-[1.08] tracking-tight text-white">
            Intelligence <span className="text-gold-gradient">meets</span> freedom
          </h1>
          <p className="mt-5 max-w-lg text-base text-neutral-400 sm:text-lg">
            पुराना इलेक्ट्रिक जादू, अब पूरी ताकत के साथ — 700+ km range, 30 min fast charging और AI copilot।
            बिजली पर चलो, लेकिन दमदार अंदाज़ में।
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link
              to="/booking/electric"
              data-testid="electric-reserve-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-[#e6b800] px-8 py-4 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] hover:shadow-[0_0_40px_rgba(230,184,0,0.5)]"
            >
              Reserve for ₹10,000
              <ArrowRight size={17} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/gallery"
              data-testid="electric-gallery-link"
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-8 py-4 font-semibold text-white transition-all duration-200 hover:border-[#e6b800]/60 hover:text-[#e6b800]"
            >
              Gallery देखें
            </Link>
          </div>
        </Reveal>
        <Reveal delay={0.12}>
          <div className="overflow-hidden rounded-2xl border border-white/10">
            <img src="/images/ev-hero.jpg" alt="IshuCar Electric sedan" className="h-full w-full object-cover" />
          </div>
          <p className="mt-4 text-right font-mono2 text-[10px] uppercase tracking-[0.3em] text-neutral-600">
            IshuCar Electric · Pearl White
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function ElectricHotspotSection() {
  const EV_HOTSPOTS = [
    { id: "ev-battery", x: 58, y: 78, tag: "Battery", title: "77 kWh LFP Battery", desc: "15-year lifetime warranty — 70%+ capacity की guarantee, 15 साल तक।" },
    { id: "ev-port", x: 30, y: 72, tag: "Charging", title: "150 kW DC Fast", desc: "10-80% सिर्फ 24 मिनट में — चाय खत्म, charging खत्म।" },
    { id: "ev-roof", x: 55, y: 31, tag: "Solar", title: "Solar Assist Roof", desc: "धूप जितनी मिले, range उतनी मुफ़्त — +15 km हर दिन।" },
    { id: "ev-cabin", x: 46, y: 45, tag: "AI", title: "Auto Drive + AI Copilot", desc: "Highway पर car खुद चले, AI आपकी आदतें सीखे।" },
  ];
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32" data-testid="hotspot-section-electric">
      <Reveal>
        <p className="eyebrow text-center">Touch to reveal the invisible</p>
        <h2 className="mt-4 text-center font-display text-3xl font-extrabold uppercase text-white sm:text-5xl">
          Electric के <span className="text-gold-gradient">राज़, छू कर देखिए</span>
        </h2>
      </Reveal>
      <Reveal className="mt-12">
        <HotspotView
          image="/images/ev-hero.jpg"
          alt="IshuCar Electric — touch to reveal"
          hotspots={EV_HOTSPOTS}
          eyebrow="Car पर points touch करें"
          testid="hotspot-view-electric"
        />
      </Reveal>
    </section>
  );
}

function RangeSection() {
  return (
    <section className="border-y border-white/10 bg-[#0a0a0e] py-24 lg:py-28" data-testid="electric-range">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          eyebrow="Range · रेंज"
          title="700+ किलोमीटर — दावा नहीं, आँकड़ा"
          sub="Delhi से Jaipur और वापस — tank की चिंता बिना। हर mode का हिसाब नीचे।"
        />
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-3">
          {RANGE.map((r, i) => (
            <Reveal key={r.mode} delay={i * 0.08} className="h-full">
              <div className="h-full rounded-2xl border border-white/10 bg-[#111116] p-7 text-center transition-colors duration-300 hover:border-[#e6b800]/50">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                  {r.mode} · {r.hindi}
                </p>
                <p className="text-gold-gradient mt-3 font-display text-4xl font-extrabold" data-testid={`range-${r.mode.toLowerCase()}`}>
                  {r.km}
                </p>
                <p className="mt-2 text-sm text-neutral-400">{r.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ChargingSection() {
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-28" data-testid="electric-charging">
      <SectionHeading
        eyebrow="Charging · चार्जिंग"
        title="चार्ज होगा तेज़, झटपट"
        sub="घर पे रात भर, highway पे चाय से कम समय — charging की टेंशन खत्म।"
      />
      <div className="mt-14 grid grid-cols-1 items-start gap-10 lg:grid-cols-5">
        <Reveal className="lg:col-span-2">
          <div className="overflow-hidden rounded-2xl border border-white/10">
            <img src="/images/ev-charging.jpg" alt="IshuCar Electric fast charging" className="w-full object-cover" />
          </div>
          <p className="mt-3 font-mono2 text-[10px] uppercase tracking-[0.3em] text-neutral-600">
            150 kW DC · Fast charge
          </p>
        </Reveal>
        <div className="space-y-4 lg:col-span-3">
          {CHARGING.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08}>
              <div className="flex gap-5 rounded-2xl border border-white/10 bg-[#111116] p-6 transition-colors duration-300 hover:border-[#e6b800]/50">
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-[#e6b800]/30 bg-[#e6b800]/10 text-[#e6b800]">
                  <c.icon size={20} />
                </span>
                <div>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-neutral-500">{c.title}</p>
                  <p className="mt-1 font-display text-xl font-bold text-white">{c.big}</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-neutral-400">{c.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function IntelligenceSection() {
  return (
    <section className="border-y border-white/10 bg-[#0a0a0e] py-24 lg:py-28" data-testid="electric-intelligence">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading
          eyebrow="Intelligence · तीन दिमाग़"
          title="गाड़ी सिर्फ़ चलती नहीं — सोचती है"
          sub="AI copilot, self-driving mode और smart connectivity — हर सफ़र smart।"
        />
        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-3">
          {INTELLIGENCE.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.08} className="h-full">
              <div className="h-full rounded-2xl border border-white/10 bg-[#111116] p-7 transition-colors duration-300 hover:border-[#e6b800]/50">
                <f.icon size={24} className="text-[#e6b800]" />
                <h3 className="mt-4 font-display text-lg font-bold text-white">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-neutral-400">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function SpecsSection() {
  return (
    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="electric-specs">
      <SectionHeading eyebrow="Specs · खूबियाँ" title="पूरा ब्योरा, एक नज़र में" sub="जो वादा किया, वही लिखा — बिना छुपे।" />
      <div className="mt-14 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {SPECS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.06}>
            <div className="h-full rounded-2xl border border-white/10 bg-[#111116] p-5 text-center transition-colors duration-300 hover:border-[#e6b800]/50">
              <s.icon size={20} className="mx-auto text-[#e6b800]" />
              <p className="mt-3 font-display text-lg font-extrabold text-white">{s.value}</p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.14em] text-neutral-500">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-16">
        <div className="card-glow relative overflow-hidden rounded-3xl border border-[#e6b800]/30 bg-gradient-to-br from-[#e6b800]/15 via-[#111116] to-[#0a0a0e] p-10 text-center sm:p-14">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, ease: EASE }}
          >
            <h3 className="font-display text-2xl font-extrabold uppercase text-white sm:text-4xl">
              Future की <span className="text-gold-gradient">सवारी</span> — आज बुक करो
            </h3>
            <p className="mx-auto mt-3 max-w-md text-sm text-neutral-400 sm:text-base">
              Refundable token {""}₹999 — Electric में अपनी जगह पक्की करो।
            </p>
            <div className="mt-8" data-testid="electric-countdown">
              <p className="mb-3 font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">
                Delivery January 2028 · बस इतने दिन बचे
              </p>
              <Countdown />
            </div>
            <Link
              to="/booking/electric"
              data-testid="electric-bottom-cta"
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#e6b800] px-9 py-4 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] hover:shadow-[0_0_40px_rgba(230,184,0,0.5)]"
            >
              Reserve your Electric
              <ArrowRight size={17} className="transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </motion.div>
        </div>
      </Reveal>
    </section>
  );
}

export default function Electric() {
  return (
    <div data-testid="electric-page">
      <Hero />
      <RangeSection />
      <ChargingSection />
      <SpinView />
      <IntelligenceSection />
      <SpecsSection />
      <ElectricHotspotSection />
    </div>
  );
}
